import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { getCurrentDate } from '../../utilities';
import { useAuth } from '../../context/AuthContext';
import './AllAbsences.css';

function NewAbsence() {
  const { userInfo } = useAuth();
  const userRole = userInfo?.role || '';

  const [students, setStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    student_id: '',
    start_date: getCurrentDate(),
    reason: 'Ziek',
    comment: '',
  });

  const token = sessionStorage.getItem('jwtToken');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStudentsLoading(false);
      return;
    }

    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/students/mystudents`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const fetchedStudents = response.data.students || response.data || [];
        setStudents(fetchedStudents);

        if (userRole === 'ouder' && fetchedStudents.length > 0) {
          setForm((prevForm) => {
            if (prevForm.student_id) return prevForm;

            return {
              ...prevForm,
              student_id: fetchedStudents[0].student_id,
            };
          });
        }
      } catch (error) {
        console.error('Fout bij ophalen studenten:', error);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };

    fetchStudents();
  }, [token, userRole]);

  const filteredStudents = students.filter((s) => {
    const fullName = `${s.firstname || ''} ${s.lastname || ''}`.toLowerCase();
    return fullName.includes(studentSearchTerm.toLowerCase());
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/absence/addabsence`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setForm({
        student_id: userRole === 'ouder' && students.length > 0 ? students[0].student_id : '',
        start_date: getCurrentDate(),
        reason: 'Ziek',
        comment: '',
      });

      setStudentSearchTerm('');

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Absentie succesvol toegevoegd',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      navigate('/absences');
    } catch (error) {
      console.error('Fout bij toevoegen absentie:', error);

      await Swal.fire({
        icon: 'error',
        title: 'Opslaan mislukt',
        text:
          error?.response?.data?.error ||
          'Fout bij toevoegen absentie.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="absence-form-page">
      <div className="absence-form-shell">
        <button type="button" className="absence-back-button" onClick={() => navigate('/absences')}><i className="bi bi-arrow-left" />Terug naar absenties</button>
        <section className="absence-form-card">
          <header className="absence-form-header"><span className="absence-form-icon"><i className="bi bi-calendar2-plus" /></span><div><span className="absence-form-eyebrow">Aanwezigheid</span><h1>Nieuwe absentie</h1><p>Registreer een afwezigheid of te-laatmelding voor een leerling.</p></div></header>
          <form className="absence-form-body" onSubmit={handleSubmit}>
        <div className="absence-field">
          <label htmlFor="absence-student">Leerling</label>

          {userRole !== 'ouder' && (
            <div className="absence-search-wrap">
              <i className="bi bi-search" />
              <input
                type="text"
                className="absence-control"
                placeholder="Zoek leerling"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
              />
            </div>
          )}

          <select
            id="absence-student"
            className="absence-control"
            value={form.student_id}
            onChange={(e) =>
              setForm((prevForm) => ({
                ...prevForm,
                student_id: e.target.value,
              }))
            }
            required
          >
            <option value="">{studentsLoading ? 'Leerlingen laden' : 'Selecteer leerling'}</option>

            {filteredStudents.map((s) => (
              <option key={s.student_id} value={s.student_id}>
                {s.firstname} {s.lastname}
              </option>
            ))}
          </select>

          {filteredStudents.length === 0 && studentSearchTerm && (
            <small className="absence-help">
              Geen leerlingen gevonden
            </small>
          )}

          {filteredStudents.length > 0 && studentSearchTerm && (
            <small className="absence-help">
              {filteredStudents.length} leerling(en) gevonden
            </small>
          )}
        </div>

        <div className="absence-field">
          <label htmlFor="absence-date">Datum</label>

          <input
            type="date"
            id="absence-date"
            className="absence-control"
            value={form.start_date}
            onChange={(e) =>
              setForm((prevForm) => ({
                ...prevForm,
                start_date: e.target.value,
              }))
            }
            required
          />
        </div>

        <div className="absence-field">
          <label htmlFor="absence-reason">Reden</label>

          <select
            id="absence-reason"
            className="absence-control"
            value={form.reason}
            onChange={(e) =>
              setForm((prevForm) => ({
                ...prevForm,
                reason: e.target.value,
              }))
            }
          >
            <option value="Ziek">Ziek</option>
            <option value="Te laat">Te laat</option>
            <option value="Verlof">Verlof</option>
            <option value="Onbekend">Onbekend</option>
          </select>
        </div>

        <div className="absence-field">
          <label htmlFor="absence-comment">Opmerking <span className="absence-help">optioneel</span></label>

          <textarea
            id="absence-comment"
            className="absence-control absence-textarea"
            rows="3"
            value={form.comment}
            onChange={(e) =>
              setForm((prevForm) => ({
                ...prevForm,
                comment: e.target.value,
              }))
            }
          />
        </div>

        <div className="absence-form-actions">
          <button
            type="button"
            className="absence-secondary-button"
            onClick={() => navigate('/absences')}
          >
            Annuleren
          </button>

          <button type="submit" className="absence-submit-button" disabled={submitting || studentsLoading}>
            {submitting ? <><span className="ab-loader" />Opslaan¦</> : <><i className="bi bi-check2-circle" />Absentie opslaan</>}
          </button>
        </div>
          </form>
        </section>
      </div>
    </main>
  );
}

export default NewAbsence;